// Near-ultrasonic MFSK codec: bytes in, mono PCM out, and back.
//
// Pure and deterministic on purpose. It takes the sample rate as a parameter
// and never touches AudioContext, getUserMedia, or the DOM, so the whole
// channel can be characterised in a test runner (add noise, add interference,
// slide the frame off alignment) instead of only on real hardware. Same rule
// the ledger core follows: no ambient state, no Date.now().
//
// Modulation: 16-tone MFSK, 4 bits per symbol, tones placed exactly on FFT bin
// centres so a Goertzel filter of the same window length sees them as
// orthogonal. The band sits just above the top of adult hearing and just below
// where phone speakers and mics fall off a cliff.
//
// Frame: [preamble 8 symbols][len 1 byte][payload len bytes][crc16 2 bytes]

export interface CodecParams {
  sampleRate: number;
  /** Samples per symbol. Derived from symbolMs so tones land on bin centres. */
  symbolSamples: number;
  /** FFT bin index of tone 0. */
  startBin: number;
  /** Bin distance between adjacent tones. 2 leaves a guard bin either side. */
  binStep: number;
  /** Peak amplitude of the emitted waveform, 0..1. */
  amplitude: number;
}

export const TONES = 16; // 4 bits per symbol
export const BITS_PER_SYMBOL = 4;
export const MAX_PAYLOAD = 255;

// A fixed, non-repeating symbol pattern. The receiver correlates against it to
// find where the frame starts; alternating patterns correlate too well with
// themselves and produce off-by-one-symbol locks.
export const PREAMBLE = [0, 15, 4, 11, 2, 13, 7, 8] as const;

const DEFAULT_SYMBOL_MS = 25;
const DEFAULT_BASE_HZ = 17600;
const RAMP_MS = 4; // fade in/out, so the burst does not click audibly

export function defaultParams(
  sampleRate: number,
  symbolMs: number = DEFAULT_SYMBOL_MS,
  baseHz: number = DEFAULT_BASE_HZ,
): CodecParams {
  if (!Number.isFinite(sampleRate) || sampleRate < 16000) {
    throw new Error("sample rate too low for a near-ultrasonic band");
  }
  const symbolSamples = Math.round((sampleRate * symbolMs) / 1000);
  const binWidth = sampleRate / symbolSamples;
  const startBin = Math.round(baseHz / binWidth);
  const params: CodecParams = { sampleRate, symbolSamples, startBin, binStep: 2, amplitude: 0.5 };
  const top = toneHz(params, TONES - 1);
  if (top >= sampleRate / 2) throw new Error("top tone exceeds Nyquist");
  return params;
}

export function toneHz(p: CodecParams, tone: number): number {
  return ((p.startBin + tone * p.binStep) * p.sampleRate) / p.symbolSamples;
}

/** Symbols in a frame carrying `payloadLen` bytes. */
export function frameSymbols(payloadLen: number): number {
  return PREAMBLE.length + 2 * (1 + payloadLen + 2);
}

/** Wall-clock seconds a frame of `payloadLen` bytes occupies. */
export function frameSeconds(p: CodecParams, payloadLen: number): number {
  return (frameSymbols(payloadLen) * p.symbolSamples) / p.sampleRate;
}

/** Payload bytes per second, excluding framing overhead. */
export function goodputBytesPerSecond(p: CodecParams, payloadLen: number): number {
  return payloadLen / frameSeconds(p, payloadLen);
}

// CRC-16/CCITT-FALSE. Two bytes is the right size here: the payload is tens of
// bytes and a bad decode must be rejected, not silently joined to.
export function crc16(bytes: Uint8Array): number {
  let crc = 0xffff;
  for (const b of bytes) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc;
}

function bytesToSymbols(bytes: Uint8Array): number[] {
  const out: number[] = [];
  for (const b of bytes) {
    out.push((b >> 4) & 0x0f, b & 0x0f);
  }
  return out;
}

function symbolsToBytes(symbols: number[]): Uint8Array {
  const out = new Uint8Array(symbols.length >> 1);
  for (let i = 0; i < out.length; i++) {
    out[i] = ((symbols[2 * i] as number) << 4) | (symbols[2 * i + 1] as number);
  }
  return out;
}

export function modulate(payload: Uint8Array, p: CodecParams): Float32Array {
  if (payload.length > MAX_PAYLOAD) throw new Error("payload too large");
  const body = new Uint8Array(1 + payload.length);
  body[0] = payload.length;
  body.set(payload, 1);
  const crc = crc16(body);
  const framed = new Uint8Array(body.length + 2);
  framed.set(body, 0);
  framed[body.length] = (crc >> 8) & 0xff;
  framed[body.length + 1] = crc & 0xff;

  const symbols = [...PREAMBLE, ...bytesToSymbols(framed)];
  const out = new Float32Array(symbols.length * p.symbolSamples);

  // Continuous phase across symbol boundaries: a phase jump splatters energy
  // across the whole band and is exactly what the guard bins are there to
  // avoid.
  let phase = 0;
  let n = 0;
  for (const tone of symbols) {
    const step = (2 * Math.PI * toneHz(p, tone)) / p.sampleRate;
    for (let i = 0; i < p.symbolSamples; i++) {
      out[n++] = p.amplitude * Math.sin(phase);
      phase += step;
      if (phase > 2 * Math.PI) phase -= 2 * Math.PI;
    }
  }

  const ramp = Math.min(Math.round((p.sampleRate * RAMP_MS) / 1000), out.length >> 1);
  for (let i = 0; i < ramp; i++) {
    const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / ramp);
    out[i] = (out[i] as number) * w;
    out[out.length - 1 - i] = (out[out.length - 1 - i] as number) * w;
  }
  return out;
}

/** Goertzel energy of one FFT bin over samples[start, start+N). */
function binEnergy(samples: Float32Array, start: number, n: number, bin: number): number {
  const coeff = 2 * Math.cos((2 * Math.PI * bin) / n);
  let s1 = 0;
  let s2 = 0;
  for (let i = 0; i < n; i++) {
    const s = (samples[start + i] as number) + coeff * s1 - s2;
    s2 = s1;
    s1 = s;
  }
  return s1 * s1 + s2 * s2 - coeff * s1 * s2;
}

function toneEnergies(samples: Float32Array, start: number, p: CodecParams): number[] {
  const e: number[] = [];
  for (let t = 0; t < TONES; t++) {
    e.push(binEnergy(samples, start, p.symbolSamples, p.startBin + t * p.binStep));
  }
  return e;
}

function argmax(values: number[]): number {
  let best = 0;
  for (let i = 1; i < values.length; i++) {
    if ((values[i] as number) > (values[best] as number)) best = i;
  }
  return best;
}

/**
 * Score how well a preamble sits at `offset`: for each preamble symbol, the
 * share of in-band energy landing on the expected tone. 1.0 is perfect, 1/16
 * is noise.
 */
function preambleScore(samples: Float32Array, offset: number, p: CodecParams): number {
  let score = 0;
  for (let i = 0; i < PREAMBLE.length; i++) {
    const e = toneEnergies(samples, offset + i * p.symbolSamples, p);
    const total = e.reduce((a, b) => a + b, 0);
    if (total <= 0) return 0;
    score += (e[PREAMBLE[i] as number] as number) / total;
  }
  return score / PREAMBLE.length;
}

/**
 * Cheap "is anything even up there" check. Full sync is a sweep over every
 * offset and costs real CPU, which matters when a phone is polling a live
 * microphone. This samples a handful of windows and compares energy on the
 * tone bins against guard bins just outside them, so a quiet room is rejected
 * for a fraction of the cost.
 */
export function bandActivity(samples: Float32Array, p: CodecParams, windows = 8): number {
  const n = p.symbolSamples;
  if (samples.length < n) return 0;
  const step = Math.max(1, Math.floor((samples.length - n) / Math.max(1, windows - 1)));
  let onTone = 0;
  let offTone = 0;
  for (let start = 0; start + n <= samples.length; start += step) {
    for (let t = 0; t < TONES; t++) {
      onTone += binEnergy(samples, start, n, p.startBin + t * p.binStep);
    }
    // Guard bins sit between the tones, where a real frame puts nothing.
    for (let t = 0; t < TONES; t++) {
      offTone += binEnergy(samples, start, n, p.startBin + t * p.binStep + 1);
    }
  }
  return offTone <= 0 ? 0 : onTone / offTone;
}

export interface SyncResult {
  offset: number;
  score: number;
}

/**
 * Find the frame start by sliding the preamble correlator. Coarse pass at
 * 1/8 symbol, then a fine pass around the winner. Returns null if nothing in
 * the recording looks like a frame.
 */
export function findFrame(
  samples: Float32Array,
  p: CodecParams,
  // Measured: a real frame scores ~0.5 at -15 dB broadband SNR, which is the
  // point where symbol decoding gives out anyway. Loosening this gate only
  // buys locks onto frames the CRC then rejects.
  minScore = 0.4,
): SyncResult | null {
  const need = PREAMBLE.length * p.symbolSamples;
  if (samples.length < need) return null;
  const limit = samples.length - need;
  const coarseHop = Math.max(1, p.symbolSamples >> 3);

  let best: SyncResult = { offset: 0, score: -1 };
  for (let o = 0; o <= limit; o += coarseHop) {
    const score = preambleScore(samples, o, p);
    if (score > best.score) best = { offset: o, score };
  }
  const fineHop = Math.max(1, coarseHop >> 3);
  for (let o = Math.max(0, best.offset - coarseHop); o <= Math.min(limit, best.offset + coarseHop); o += fineHop) {
    const score = preambleScore(samples, o, p);
    if (score > best.score) best = { offset: o, score };
  }
  return best.score >= minScore ? best : null;
}

export interface DecodeFailure {
  ok: false;
  reason: "no frame" | "truncated" | "bad crc";
}
export interface DecodeSuccess {
  ok: true;
  payload: Uint8Array;
  offset: number;
  score: number;
}
export type DecodeResult = DecodeSuccess | DecodeFailure;

export function demodulate(samples: Float32Array, p: CodecParams): DecodeResult {
  const sync = findFrame(samples, p);
  if (sync === null) return { ok: false, reason: "no frame" };

  const readSymbols = (start: number, count: number): number[] | null => {
    if (start + count * p.symbolSamples > samples.length) return null;
    const out: number[] = [];
    for (let i = 0; i < count; i++) {
      out.push(argmax(toneEnergies(samples, start + i * p.symbolSamples, p)));
    }
    return out;
  };

  const bodyStart = sync.offset + PREAMBLE.length * p.symbolSamples;
  const lenSymbols = readSymbols(bodyStart, 2);
  if (lenSymbols === null) return { ok: false, reason: "truncated" };
  const len = (symbolsToBytes(lenSymbols)[0] as number);

  const rest = readSymbols(bodyStart + 2 * p.symbolSamples, 2 * (len + 2));
  if (rest === null) return { ok: false, reason: "truncated" };
  const restBytes = symbolsToBytes(rest);

  const body = new Uint8Array(1 + len);
  body[0] = len;
  body.set(restBytes.subarray(0, len), 1);
  const got = ((restBytes[len] as number) << 8) | (restBytes[len + 1] as number);
  if (got !== crc16(body)) return { ok: false, reason: "bad crc" };

  return { ok: true, payload: restBytes.slice(0, len), offset: sync.offset, score: sync.score };
}
